import type { ReportElementType } from '@highimpact-frontend/api-types';
import { useCallback } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';
import {
  useDashboardLibrarySelector,
} from './DashboardLibraryProvider';
import {
  useWidgetInstanceRegistry,
  useWidgetInstanceRegistryVersion,
} from './instanceRegistry/WidgetInstanceRegistryProvider';
import { resolveReportExportDisplayName } from './instanceRegistry/resolveReportExportDisplayName';
import type {
  ReportExportMode,
  ReportGatherOptions,
  ReportWidgetExportResult,
} from './instanceRegistry/types';
import { readWidgetFromSlice } from './sliceReaders';

// -- hook --

/**
 * Report content extraction interface fed by the widget instance registry.
 * 
 * Provide an interface to:
 * * Gather widget content for a given list of report elements
 *     * in the format expected by these report elements,
 *     * and in the given widget content extraction mode (sequential or parallel)
 * * Check if any of the widgets matching the report elements has interactive content extraction capabilities.
 *   This is useful to determine if extraction can be performed in parallel or should be sequential.
 */
export function useReportGather() {
  const registry = useWidgetInstanceRegistry();
  const registryVersion = useWidgetInstanceRegistryVersion();
  const { federationHost } = useDashboardHost();
  const dashboardState = useDashboardLibrarySelector((state) => state.dashboard);

  const getWidgetByTemplateVariable = useCallback(
    (templateVariable: string) => readWidgetFromSlice(dashboardState, templateVariable),
    [dashboardState]
  );

  const gatherReportExports = useCallback(
    (
      elements: ReportElementType[],
      mode: ReportExportMode,
      options: Omit<ReportGatherOptions, 'legacyExportContext' | 'resolveDisplayName'> = {}
    ): Promise<ReportWidgetExportResult[]> =>
      registry.gatherReportExports(elements, mode, {
        ...options,
        resolveDisplayName: (element) =>
          resolveReportExportDisplayName(element, {
            getWidgetByTemplateVariable,
          }),
        legacyExportContext: {
          executeRemoteFunction: federationHost?.executeRemoteFunction,
        },
      }),
    [federationHost?.executeRemoteFunction, getWidgetByTemplateVariable, registry]
  );

  const hasInteractiveReportExportsForElements = useCallback(
    (elements: ReportElementType[]) =>
      registry.hasInteractiveReportExportsForElements(elements),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registry, registryVersion]
  );

  return {
    gatherReportExports,
    hasInteractiveReportExportsForElements,
  };
}
